import { type FormEvent, useEffect, useState } from 'react';
import { AvatarView } from '../avatar/AvatarView';
import { Bubble } from '../components/Bubble';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import type { Slot } from '../models/types';
import { basicAvatar } from '../models/types';
import { ApiException } from '../services/apiClient';
import { useAuth } from '../state/auth';
import { usePlayer } from '../state/player';
import { useShell } from '../state/shell';
import { colors, withAlpha } from '../theme';
import { WardrobeSheet } from './WardrobeSheet';

const ALLOWED = /^[A-Za-z0-9 _]+$/;

export function validateName(v: string): string | null {
  const t = v.trim();
  if (t.length < 2) return 'At least 2 characters';
  if (t.length > 24) return 'At most 24 characters';
  if (!ALLOWED.test(t)) return 'Letters, digits, spaces and underscores only';
  return null;
}

export function ProfileScreen() {
  const player = usePlayer((s) => s.player);
  const showToast = useShell((s) => s.showToast);
  const signOut = useAuth((s) => s.signOut);
  const install = useInstallPrompt();
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [wardrobe, setWardrobe] = useState<Slot | null | false>(false);

  // Seed the field once the profile arrives.
  const serverName = player?.profile.displayName;
  useEffect(() => {
    if (serverName != null) setName((cur) => (cur === '' ? serverName : cur));
  }, [serverName]);

  const error = touched ? validateName(name) : null;
  const stats = player?.profile.stats;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (validateName(name)) return;
    setSaving(true);
    setServerError(null);
    try {
      await usePlayer.getState().updateDisplayName(name.trim());
      showToast('Name updated ✨');
    } catch (err) {
      setServerError(err instanceof ApiException ? err.message : 'Could not save right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="screen" aria-label="You">
      <h1 className="app-bar">You</h1>
      <div className="scroll">
        <div className="stack">
          <Bubble testId="profile-avatar-bubble" style={{ textAlign: 'center' }}>
            <AvatarView avatar={player?.avatar ?? basicAvatar()} size={280} flippable onSlotTap={player ? (slot) => setWardrobe(slot) : undefined} />
            <h2 className="t-headline" style={{ fontWeight: 900, margin: '8px 0 12px' }}>{player?.profile.displayName ?? ''}</h2>
            <button type="button" className="btn filled" disabled={!player} onClick={() => setWardrobe(null)}>👕 Change outfit</button>
          </Bubble>

          {stats && (
            <Bubble color={withAlpha(colors.lemon, 0.5)}>
              <h2 className="t-title-l" style={{ marginBottom: 12 }}>Stats</h2>
              <Stat label="🛰️ Scans" value={stats.scans} />
              <Stat label="✨ Finds" value={stats.finds} />
              <Stat label="🎒 Picked up" value={stats.pickups} />
              <Stat label="🧩 Hunts completed" value={stats.huntsCompleted} />
            </Bubble>
          )}

          <Bubble>
            <form onSubmit={(e) => void save(e)} noValidate>
              <label className="t-title-l" htmlFor="display-name" style={{ display: 'block', marginBottom: 12 }}>Display name</label>
              <input
                id="display-name"
                className={`input${error || serverError ? ' invalid' : ''}`}
                value={name}
                maxLength={24}
                placeholder="Hunter"
                autoComplete="nickname"
                aria-invalid={Boolean(error || serverError)}
                aria-describedby="display-name-error"
                onChange={(e) => {
                  setName(e.target.value);
                  setTouched(true);
                  setServerError(null);
                }}
              />
              <div id="display-name-error" className="field-error" role="alert">{error ?? serverError}</div>
              <button type="submit" className="btn filled" style={{ marginTop: 12 }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </form>
          </Bubble>

          {install.canPrompt && (
            <button type="button" className="btn tonal block" onClick={() => void install.prompt()}>📲 Install EthrHunt</button>
          )}
          {install.showIosHint && (
            <Bubble color={withAlpha(colors.sky, 0.25)}>
              <p>Install EthrHunt: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</p>
            </Bubble>
          )}

          <button type="button" className="btn outlined block" onClick={() => void signOut()}>⎋ Sign out</button>
        </div>
      </div>
      {wardrobe !== false && <WardrobeSheet initialSlot={wardrobe ?? 'head'} onClose={() => setWardrobe(false)} />}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-row">
      <span>{label}</span>
      <span className="t-title-m">{value}</span>
    </div>
  );
}
